import React, { useState, useEffect } from 'react';
import FlippexLoader from './ui/FlippexLoader';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth, api } from '../context/AuthContext';
import { 
  Lock, DollarSign, TrendingUp, Building, FileText, 
  PiggyBank, Receipt, Calculator, ChevronDown, ChevronUp,
  ExternalLink, Percent, AlertCircle, Landmark, HardHat,
  Scale, ShieldCheck, MoreHorizontal, Home, Wrench, Lightbulb,
  Clock, TrendingDown, ArrowUpRight, ArrowDownRight, Minus, X, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { normalizeProjectType, isRentalStrategy } from '../lib/projectTypes';
import PhaseFinancialsSection from './project/PhaseFinancialsSection';

// Modal Tooltip Component for APY explanation
const ModalTooltip = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative bg-slate-800 border border-white/20 rounded-xl p-6 max-w-sm w-full shadow-2xl animate-in fade-in-0 zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-foreground-muted hover:text-white" />
        </button>
        
        {/* Content */}
        {title && (
          <h4 className="font-semibold text-white mb-3 pr-8">{title}</h4>
        )}
        <p className="text-sm text-foreground-muted leading-relaxed">{content}</p>
      </div>
    </div>
  );
};

// Category labels and icons
const BUDGET_CATEGORIES = {
  acquisition: { label: 'Acquisition', icon: Landmark },
  renovation: { label: 'Renovation', icon: HardHat },
  legal: { label: 'Legal & Compliance', icon: Scale },
  taxes: { label: 'Taxes & Duties', icon: Receipt },
  contingency: { label: 'Contingency', icon: ShieldCheck },
  fees: { label: 'Fees', icon: Receipt },
  other: { label: 'Other', icon: MoreHorizontal },
};

const OPERATING_EXPENSE_CATEGORIES = {
  property_management: { label: 'Property Management', icon: Building },
  maintenance: { label: 'Maintenance', icon: Wrench },
  insurance: { label: 'Insurance', icon: ShieldCheck },
  utilities: { label: 'Utilities', icon: Lightbulb },
  taxes: { label: 'Property Taxes', icon: Receipt },
  other: { label: 'Other', icon: MoreHorizontal },
};

const DOCUMENT_TYPES = {
  legal: { label: 'Legal', icon: Scale },
  financial: { label: 'Financial', icon: DollarSign },
  technical: { label: 'Technical', icon: HardHat },
  report: { label: 'Report', icon: FileText },
  other: { label: 'Other', icon: MoreHorizontal },
};

// Project type helpers - strategies are normalised, see lib/projectTypes.js
const hasExitPnL = (projectType) => {
  return ['renovate_sell', 'new_development_sale', 'hybrid'].includes(normalizeProjectType(projectType));
};

const hasOperatingPnL = (projectType) => {
  return isRentalStrategy(projectType);
};

const isHybrid = (projectType) => {
  return normalizeProjectType(projectType) === 'hybrid';
};

// Format currency helper (moved outside component for reuse)
const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

// Format percentage helper
const formatPercent = (value) => {
  if (value === null || value === undefined) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

// Calculate variance helper
const calculateVariance = (actual, budget) => {
  if (budget === 0) return { amount: actual, percent: null };
  const amount = actual - budget;
  const percent = (amount / Math.abs(budget)) * 100;
  return { amount, percent };
};

// Variance display component (moved outside to avoid re-creation on each render)
const VarianceDisplay = ({ actual, budget, invertColors = false }) => {
  const variance = calculateVariance(actual || 0, budget || 0);
  const isPositive = variance.amount > 0;
  const isNegative = variance.amount < 0;
  
  // For costs, negative variance (under budget) is good
  // For revenue, positive variance (over target) is good
  const isGood = invertColors ? isPositive : isNegative;
  const isBad = invertColors ? isNegative : isPositive;
  
  return (
    <div className="flex items-center gap-1 text-xs">
      {variance.amount !== 0 ? (
        <>
          {isGood && <ArrowDownRight className="h-3 w-3 text-green-400" />}
          {isBad && <ArrowUpRight className="h-3 w-3 text-red-400" />}
          <span className={isGood ? 'text-green-400' : isBad ? 'text-red-400' : 'text-foreground-muted'}>
            {formatCurrency(Math.abs(variance.amount))}
            {variance.percent !== null && ` (${formatPercent(variance.percent)})`}
          </span>
        </>
      ) : (
        <>
          <Minus className="h-3 w-3 text-foreground-muted" />
          <span className="text-foreground-muted">On budget</span>
        </>
      )}
    </div>
  );
};

const ProjectFinancialsSection = ({ projectId }) => {
  const { user, setShowAuthModal } = useAuth();
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedActualCategories, setExpandedActualCategories] = useState({});
  const [showApyModal, setShowApyModal] = useState(false);

  useEffect(() => {
    const fetchFinancials = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/projects/${projectId}/financials`);
        setFinancials(response.data);
      } catch (error) {
        // 404 simply means this project has no deal-room financials configured yet
        if (error.response?.status !== 404) {
          console.error('Error fetching financials:', error);
          toast.error('Failed to load financial data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFinancials();
  }, [projectId, user]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleActualCategory = (category) => {
    setExpandedActualCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleLoginClick = () => {
    setShowAuthModal(true);
  };

  if (loading) {
    return (
      <Card className="glass p-6">
        <div className="flex items-center justify-center py-8">
          <FlippexLoader size={48} animate={false} />
        </div>
      </Card>
    );
  }

  if (!financials) {
    return (
      <PhaseFinancialsSection projectId={projectId} emptyMessage="Financial data not available" />
    );
  }

  const isLoggedIn = financials.is_logged_in;
  const summary = financials.financial_summary;
  const projectType = financials.project_type || 'other';
  const hasActuals = financials.has_actuals || false;
  const actualsData = financials.actuals || null;

  // Render Financial Summary Tiles (4 columns on desktop, 2x2 on mobile)
  const renderFinancialSummary = () => (
    <Card className="glass p-6">
      <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mb-4 flex items-center gap-2">
        <Calculator className="h-5 w-5 text-primary" />
        Project Summary
      </h3>

      {/* 4-column grid on desktop (lg), 2x2 on mobile/tablet */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Raise */}
        <div className="p-3 bg-white/5 rounded-lg">
          <div className="text-[10px] sm:text-xs text-foreground-muted mb-1">Total Raise</div>
          <div className="text-base sm:text-lg font-bold">{formatCurrency(summary.target_raise)}</div>
          {summary.buffer_percentage > 0 && (
            <div className="text-[10px] sm:text-xs text-amber-400 mt-1">
              +{summary.buffer_percentage.toFixed(1)}% buffer
            </div>
          )}
        </div>

        {/* Target APY with modal tooltip */}
        <div className="p-3 bg-white/5 rounded-lg">
          <div className="text-[10px] sm:text-xs text-foreground-muted mb-1 flex items-center gap-1">
            <span>Target APY</span>
            <button
              onClick={() => setShowApyModal(true)}
              className="p-0.5 rounded-full hover:bg-white/10 transition-colors"
              aria-label="What is Target APY?"
            >
              <Info className="h-3 w-3 text-foreground-muted hover:text-primary" />
            </button>
          </div>
          <div className="text-base sm:text-lg font-bold text-accent">{parseFloat(summary.target_yield || 0).toFixed(1)}%</div>
        </div>

        {/* Duration */}
        <div className="p-3 bg-white/5 rounded-lg">
          <div className="text-[10px] sm:text-xs text-foreground-muted mb-1">Duration</div>
          <div className="text-base sm:text-lg font-bold">{financials.duration_months} mo</div>
        </div>

        {/* Expected Net */}
        <div className="p-3 bg-white/5 rounded-lg">
          <div className="text-[10px] sm:text-xs text-foreground-muted mb-1">{summary.expected_net_label || 'Expected Net'}</div>
          <div className={`text-base sm:text-lg font-bold ${summary.expected_net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {formatCurrency(summary.expected_net)}
          </div>
        </div>
      </div>

      {/* APY Modal Tooltip */}
      <ModalTooltip
        isOpen={showApyModal}
        onClose={() => setShowApyModal(false)}
        title="Target APY"
        content="APY is an annualized percentage yield target estimate. Actual returns may differ and depend on execution, timing, and market conditions."
      />

      {/* Disclaimer */}
      <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
        <div className="flex items-start gap-2 text-xs text-amber-200">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{summary.disclaimer}</span>
        </div>
      </div>
    </Card>
  );

  // Render Budget CAPEX section (reusing Sources & Uses structure)
  const renderBudgetCapex = () => (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground-muted uppercase tracking-wide">
        <Building className="h-4 w-4" /> Budget CAPEX
      </h4>
      
      {financials.budget_breakdown?.length === 0 ? (
        <p className="text-foreground-muted text-center py-4 text-sm">No budget items available</p>
      ) : (
        <div className="space-y-2">
          {financials.budget_breakdown?.map((category) => {
            const CategoryIcon = BUDGET_CATEGORIES[category.category]?.icon || MoreHorizontal;
            const isExpanded = expandedCategories[category.category];
            
            return (
              <div key={category.category} className="border border-white/10 rounded-lg overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors"
                  onClick={() => toggleCategory(category.category)}
                >
                  <div className="flex items-center gap-2">
                    <CategoryIcon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">{category.category_label}</span>
                    <Badge variant="outline" className="text-xs">
                      {category.items.length}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold">{formatCurrency(category.subtotal)}</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="p-3 space-y-2">
                    {category.items.map((item) => (
                      <div key={item.id} className="flex justify-between text-xs py-1 border-b border-white/5 last:border-0">
                        <span className="text-foreground-muted">{item.description || item.subcategory || 'Line item'}</span>
                        <span>{formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add Fees to Budget CAPEX if exists */}
          {financials.fee_schedules?.length > 0 && (
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <button
                className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors"
                onClick={() => toggleCategory('fees')}
              >
                <div className="flex items-center gap-2">
                  <Receipt className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Fees</span>
                  <Badge variant="outline" className="text-xs">
                    {financials.fee_schedules.length}
                  </Badge>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold">{formatCurrency(financials.financial_summary.total_fees)}</span>
                  {expandedCategories['fees'] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </button>
              
              {expandedCategories['fees'] && (
                <div className="p-3 space-y-2">
                  {financials.fee_schedules.map((fee) => (
                    <div key={fee.id} className="flex justify-between text-xs py-1 border-b border-white/5 last:border-0">
                      <div>
                        <span className="text-foreground-muted capitalize">{fee.fee_type.replace(/_/g, ' ')}</span>
                        {fee.basis === 'percent_capex' && (
                          <span className="text-foreground-muted/60 ml-1">({fee.rate_or_amount}% of CAPEX)</span>
                        )}
                      </div>
                      <span>{formatCurrency(fee.calculated_amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Total CAPEX */}
          <div className="flex justify-between p-3 bg-primary/10 rounded-lg mt-4">
            <span className="text-sm font-bold">Total Budget CAPEX</span>
            <span className="text-sm font-bold">{formatCurrency((financials.capex_subtotal || 0) + (financials.financial_summary.total_fees || 0))}</span>
          </div>
        </div>
      )}
    </div>
  );

  // Render Target Exit P&L (for flipping, new_development, hybrid)
  const renderExitPnL = () => {
    if (!hasExitPnL(projectType)) return null;
    
    const pnl = financials.project_pnl;
    if (!pnl) return null;

    return (
      <div className="space-y-3">
        <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground-muted uppercase tracking-wide">
          <TrendingUp className="h-4 w-4" /> Target Exit P&L
        </h4>
        <div className="space-y-2 bg-white/5 rounded-lg p-4">
          <div className="flex justify-between py-2 border-b border-white/10 text-sm">
            <span className="text-foreground-muted">Total CAPEX</span>
            <span className="font-medium">{formatCurrency(pnl.total_capex)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/10 text-sm">
            <span className="text-foreground-muted">Total Fees</span>
            <span className="font-medium">{formatCurrency(pnl.total_fees)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/10 bg-white/5 -mx-4 px-4 text-sm">
            <span className="font-medium">Target Raise</span>
            <span className="font-bold">{formatCurrency(pnl.target_raise)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/10 text-sm">
            <span className="text-foreground-muted">Net Revenue</span>
            <span className="font-medium">{formatCurrency(pnl.estimated_gross_revenue)}</span>
          </div>
          <div className="flex justify-between py-2 bg-primary/10 -mx-4 px-4 rounded text-sm">
            <span className="font-bold">Net Profit</span>
            <span className={`font-bold ${pnl.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(pnl.net_profit)}
            </span>
          </div>
          {pnl.target_raise > 0 && (
            <div className="flex justify-between py-2 text-sm">
              <span className="text-foreground-muted">ROI</span>
              <span className="font-medium text-accent">
                {((pnl.net_profit / pnl.target_raise) * 100).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Target Operating P&L (for hold_yield, hybrid)
  const renderOperatingPnL = () => {
    if (!hasOperatingPnL(projectType)) return null;
    
    const opPnl = financials.operating_pnl;
    if (!opPnl) return null;

    return (
      <div className="space-y-3">
        <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground-muted uppercase tracking-wide">
          <Home className="h-4 w-4" /> Target Operating P&L (Annual)
        </h4>
        <div className="space-y-2 bg-white/5 rounded-lg p-4">
          <div className="flex justify-between py-2 border-b border-white/10 text-sm">
            <span className="text-foreground-muted">Gross Revenue</span>
            <span className="font-medium">{formatCurrency(opPnl.annual_gross_revenue)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/10 text-sm">
            <span className="text-foreground-muted">Operating Expenses</span>
            <span className="font-medium text-red-400">
              -{formatCurrency(opPnl.annual_operating_expenses)}
            </span>
          </div>
          <div className="flex justify-between py-2 bg-accent/10 -mx-4 px-4 rounded text-sm">
            <span className="font-bold">NOI (Net Operating Income)</span>
            <span className={`font-bold ${opPnl.noi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(opPnl.noi)}
            </span>
          </div>
        </div>
        
        {/* Monthly breakdown */}
        {financials.estimated_monthly_revenue && (
          <div className="p-3 bg-white/5 rounded-lg text-xs">
            <div className="text-foreground-muted">
              Monthly Revenue: {formatCurrency(financials.estimated_monthly_revenue)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Budget Investment & Performance section (Phase 3)
  const renderBudgetAndPnL = () => (
    <Card className="glass p-6">
      <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mb-1 flex items-center gap-2">
        <PiggyBank className="h-5 w-5 text-primary" />
        Budget Investment & Performance
      </h3>
      <p className="text-xs text-foreground-muted mb-6">Budget CAPEX and P&L</p>

      <div className="space-y-8">
        {/* Budget CAPEX */}
        {renderBudgetCapex()}

        {/* Budget P&L - Conditional by project type */}
        <div className="border-t border-white/10 pt-6">
          <h4 className="text-sm font-semibold mb-4 text-foreground-muted uppercase tracking-wide">Budget P&L</h4>
          
          {isHybrid(projectType) ? (
            // Hybrid shows both Operating and Exit P&L
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderOperatingPnL()}
              {renderExitPnL()}
            </div>
          ) : hasOperatingPnL(projectType) ? (
            // Hold/Yield shows only Operating P&L
            renderOperatingPnL()
          ) : hasExitPnL(projectType) ? (
            // Flipping/New Dev shows only Exit P&L
            renderExitPnL()
          ) : (
            // Fallback for other project types
            renderExitPnL()
          )}
        </div>
      </div>

      {/* Footnote */}
      <div className="mt-6 pt-4 border-t border-white/10">
        <p className="text-xs text-foreground-muted italic">
          For full detail, see Documents → Business Plan Document (full budget line items) and Project Reports (when available).
        </p>
      </div>
    </Card>
  );

  // Render Actual P&L section (Phase 4) - Updated for canonical actuals format
  const renderActualPnL = () => {
    // Check if actuals exist and are published
    if (!hasActuals || !actualsData) {
      // Show empty state or "not published" message
      const notPublishedMsg = financials.actuals_not_published_message;
      return (
        <Card className="glass p-6">
          <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Actual Performance
          </h3>
          
          {/* Empty state disclosure */}
          <div className="py-8 text-center">
            <Clock className="h-12 w-12 mx-auto text-foreground-muted mb-4 opacity-50" />
            <p className="text-foreground-muted">
              {notPublishedMsg || 'This project is still under development. Actuals will be published once available.'}
            </p>
          </div>
        </Card>
      );
    }

    // Check for legacy format vs new canonical format
    const isCanonicalFormat = actualsData.capex_variance || actualsData.operating_pnl_variance || actualsData.exit_pnl_variance;
    
    // Render canonical format (new)
    if (isCanonicalFormat) {
      const summary = actualsData.summary || {};
      
      return (
        <Card className="glass p-6">
          <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mb-1 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Actual Performance
          </h3>
          {actualsData.published_at && (
            <p className="text-xs text-foreground-muted mb-4">
              Published: {new Date(actualsData.published_at).toLocaleDateString()}
            </p>
          )}

          <div className="space-y-6">
            {/* Performance Summary Tiles */}
            {(summary.roi_percent !== null || summary.apy_percent !== null || summary.actual_days) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {summary.roi_percent !== null && (
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="text-xs text-foreground-muted mb-1">ROI</div>
                    <div className={`text-lg font-bold ${summary.roi_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {summary.roi_percent >= 0 ? '+' : ''}{summary.roi_percent?.toFixed(1)}%
                    </div>
                  </div>
                )}
                {summary.apy_percent !== null && (
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="text-xs text-foreground-muted mb-1">APY</div>
                    <div className={`text-lg font-bold ${summary.apy_percent >= 0 ? 'text-accent' : 'text-red-400'}`}>
                      {summary.apy_percent >= 0 ? '+' : ''}{summary.apy_percent?.toFixed(1)}%
                    </div>
                    {summary.apy_calculation_note && (
                      <div className="text-xs text-foreground-muted">{summary.apy_calculation_note}</div>
                    )}
                  </div>
                )}
                {summary.actual_days && (
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="text-xs text-foreground-muted mb-1">Duration</div>
                    <div className="text-lg font-bold">{summary.actual_days} days</div>
                  </div>
                )}
                {summary.total_actual_profit !== undefined && (
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="text-xs text-foreground-muted mb-1">Net Profit</div>
                    <div className={`text-lg font-bold ${summary.total_actual_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(summary.total_actual_profit)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Actual CAPEX Variance */}
            {actualsData.capex_variance && actualsData.capex_variance.items?.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground-muted uppercase tracking-wide">
                  <Building className="h-4 w-4" /> CAPEX: Budget vs Actual
                </h4>
                
                {/* Header row */}
                <div className="grid grid-cols-4 gap-2 text-xs text-foreground-muted font-medium px-3 py-2 bg-white/5 rounded-t-lg">
                  <div>Item</div>
                  <div className="text-right">Budget</div>
                  <div className="text-right">Actual</div>
                  <div className="text-right">Variance</div>
                </div>

                {/* CAPEX line items */}
                <div className="space-y-1">
                  {actualsData.capex_variance.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-2 text-xs px-3 py-2 border-b border-white/5">
                      <div className="font-medium truncate" title={item.label}>{item.label}</div>
                      <div className="text-right text-foreground-muted">{formatCurrency(item.budget)}</div>
                      <div className="text-right">{formatCurrency(item.actual)}</div>
                      <div className="text-right">
                        <VarianceDisplay actual={item.actual} budget={item.budget} />
                      </div>
                    </div>
                  ))}
                  
                  {/* Total row */}
                  <div className="grid grid-cols-4 gap-2 text-xs px-3 py-3 bg-primary/10 rounded-b-lg font-bold">
                    <div>Total CAPEX</div>
                    <div className="text-right">{formatCurrency(actualsData.capex_variance.budget_total)}</div>
                    <div className="text-right">{formatCurrency(actualsData.capex_variance.actual_total)}</div>
                    <div className="text-right">
                      <VarianceDisplay actual={actualsData.capex_variance.actual_total} budget={actualsData.capex_variance.budget_total} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Operating P&L Variance (for Hold/Yield projects) */}
            {actualsData.operating_pnl_variance && actualsData.operating_pnl_variance.items?.length > 0 && (
              <div className="space-y-3 border-t border-white/10 pt-6">
                <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground-muted uppercase tracking-wide">
                  <Home className="h-4 w-4" /> Operating P&L: Budget vs Actual
                </h4>
                
                {/* Header row */}
                <div className="grid grid-cols-4 gap-2 text-xs text-foreground-muted font-medium px-3 py-2 bg-white/5 rounded-t-lg">
                  <div>Item</div>
                  <div className="text-right">Budget</div>
                  <div className="text-right">Actual</div>
                  <div className="text-right">Variance</div>
                </div>

                {/* P&L line items */}
                <div className="space-y-1">
                  {actualsData.operating_pnl_variance.items.map((item, idx) => {
                    const isRevenue = item.category === 'Gross Revenue' || item.category?.toLowerCase().includes('revenue');
                    return (
                      <div key={idx} className="grid grid-cols-4 gap-2 text-xs px-3 py-2 border-b border-white/5">
                        <div className="font-medium truncate" title={item.label}>{item.label}</div>
                        <div className="text-right text-foreground-muted">{formatCurrency(item.budget)}</div>
                        <div className="text-right">{formatCurrency(item.actual)}</div>
                        <div className="text-right">
                          <VarianceDisplay actual={item.actual} budget={item.budget} invertColors={isRevenue} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* NOI Summary */}
                {(summary.operating_noi_actual !== undefined || summary.operating_noi_budget !== undefined) && (
                  <div className="grid grid-cols-4 gap-2 text-xs px-3 py-3 bg-accent/10 rounded-lg font-bold mt-2">
                    <div>NOI (Net Operating Income)</div>
                    <div className="text-right">{formatCurrency(summary.operating_noi_budget)}</div>
                    <div className="text-right">{formatCurrency(summary.operating_noi_actual)}</div>
                    <div className="text-right">
                      <VarianceDisplay actual={summary.operating_noi_actual} budget={summary.operating_noi_budget} invertColors={true} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Exit P&L Variance (for Flipping/New Dev projects) */}
            {actualsData.exit_pnl_variance && actualsData.exit_pnl_variance.items?.length > 0 && (
              <div className="space-y-3 border-t border-white/10 pt-6">
                <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground-muted uppercase tracking-wide">
                  <Calculator className="h-4 w-4" /> Exit P&L: Budget vs Actual
                </h4>
                
                {/* Header row */}
                <div className="grid grid-cols-4 gap-2 text-xs text-foreground-muted font-medium px-3 py-2 bg-white/5 rounded-t-lg">
                  <div>Item</div>
                  <div className="text-right">Budget</div>
                  <div className="text-right">Actual</div>
                  <div className="text-right">Variance</div>
                </div>

                {/* P&L line items */}
                <div className="space-y-1">
                  {actualsData.exit_pnl_variance.items.map((item, idx) => {
                    const isRevenue = item.category === 'Sale Proceeds' || item.category?.toLowerCase().includes('proceeds');
                    return (
                      <div key={idx} className="grid grid-cols-4 gap-2 text-xs px-3 py-2 border-b border-white/5">
                        <div className="font-medium truncate" title={item.label}>{item.label}</div>
                        <div className="text-right text-foreground-muted">{formatCurrency(item.budget)}</div>
                        <div className="text-right">{formatCurrency(item.actual)}</div>
                        <div className="text-right">
                          <VarianceDisplay actual={item.actual} budget={item.budget} invertColors={isRevenue} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footnote */}
          <div className="mt-6 pt-4 border-t border-white/10">
            <p className="text-xs text-foreground-muted italic">
              Actuals reflect real financial performance. For full detail, see Documents → Project Reports.
            </p>
          </div>
        </Card>
      );
    }

    // Render legacy format (old) - keep for backward compatibility
    return (
      <Card className="glass p-6">
        <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mb-1 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Actual P&L
        </h3>
        {actualsData.annualization_note && (
          <p className="text-xs text-amber-400 mb-4 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {actualsData.annualization_note}
          </p>
        )}

        <div className="space-y-6">
          {/* Actual CAPEX with variance (legacy) */}
          {actualsData.capex && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground-muted uppercase tracking-wide">
                <Building className="h-4 w-4" /> Actual CAPEX
              </h4>
              
              {/* Header row */}
              <div className="grid grid-cols-4 gap-2 text-xs text-foreground-muted font-medium px-3 py-2 bg-white/5 rounded-t-lg">
                <div>Category</div>
                <div className="text-right">Budget</div>
                <div className="text-right">Actual</div>
                <div className="text-right">Variance</div>
              </div>

              {/* CAPEX line items */}
              <div className="space-y-1">
                {actualsData.capex.categories?.map((cat) => (
                  <div key={cat.category} className="grid grid-cols-4 gap-2 text-xs px-3 py-2 border-b border-white/5">
                    <div className="font-medium">{cat.category_label}</div>
                    <div className="text-right text-foreground-muted">{formatCurrency(cat.budget)}</div>
                    <div className="text-right">{formatCurrency(cat.actual)}</div>
                    <div className="text-right">
                      <VarianceDisplay actual={cat.actual} budget={cat.budget} />
                    </div>
                  </div>
                ))}
                
                {/* Total row */}
                <div className="grid grid-cols-4 gap-2 text-xs px-3 py-3 bg-primary/10 rounded-b-lg font-bold">
                  <div>Total CAPEX</div>
                  <div className="text-right">{formatCurrency(actualsData.capex.budget_total)}</div>
                  <div className="text-right">{formatCurrency(actualsData.capex.actual_total)}</div>
                  <div className="text-right">
                    <VarianceDisplay actual={actualsData.capex.actual_total} budget={actualsData.capex.budget_total} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actual P&L with variance (legacy) */}
          {actualsData.pnl && (
            <div className="space-y-3 border-t border-white/10 pt-6">
              <h4 className="text-sm font-semibold flex items-center gap-2 text-foreground-muted uppercase tracking-wide">
                <Calculator className="h-4 w-4" /> Actual P&L
              </h4>
              
              {/* Header row */}
              <div className="grid grid-cols-4 gap-2 text-xs text-foreground-muted font-medium px-3 py-2 bg-white/5 rounded-t-lg">
                <div>Line Item</div>
                <div className="text-right">Budget</div>
                <div className="text-right">Actual</div>
                <div className="text-right">Variance</div>
              </div>

              {/* P&L line items */}
              <div className="space-y-1">
                {actualsData.pnl.line_items?.map((item, idx) => (
                  <div key={idx} className={`grid grid-cols-4 gap-2 text-xs px-3 py-2 border-b border-white/5 ${item.is_total ? 'bg-primary/10 font-bold' : ''}`}>
                    <div className={item.is_total ? 'font-bold' : 'text-foreground-muted'}>{item.label}</div>
                    <div className="text-right">{formatCurrency(item.budget)}</div>
                    <div className="text-right">{formatCurrency(item.actual)}</div>
                    <div className="text-right">
                      <VarianceDisplay actual={item.actual} budget={item.budget} invertColors={item.is_revenue} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footnote */}
        <div className="mt-6 pt-4 border-t border-white/10">
          <p className="text-xs text-foreground-muted italic">
            For full detail, see Documents → Business Plan Document (full budget line items) and Project Reports (when available).
          </p>
        </div>
      </Card>
    );
  };

  // Render Documents section (Phase 6)
  const renderDocuments = () => (
    <Card className="glass p-6">
      <h3 className="text-base sm:text-lg md:text-xl font-heading font-semibold mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5 text-primary" />
        Documents
      </h3>
      
      {financials.documents?.length === 0 ? (
        <p className="text-foreground-muted text-center py-4 text-sm">No documents available yet</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {financials.documents?.map((doc) => {
            const DocIcon = DOCUMENT_TYPES[doc.doc_type]?.icon || FileText;
            return (
              <a
                key={doc.id}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
              >
                <DocIcon className="h-5 w-5 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{doc.title}</div>
                  <div className="text-xs text-foreground-muted capitalize">
                    {DOCUMENT_TYPES[doc.doc_type]?.label || doc.doc_type}
                    {doc.date && ` • ${doc.date}`}
                  </div>
                </div>
                <ExternalLink className="h-4 w-4 text-foreground-muted flex-shrink-0" />
              </a>
            );
          })}
        </div>
      )}
    </Card>
  );

  // Render Locked Panel for non-logged-in users
  const renderLockedPanel = () => (
    <Card className="glass p-6 border-dashed border-2 border-white/20">
      <div className="text-center py-4">
        <Lock className="h-12 w-12 mx-auto text-foreground-muted mb-4" />
        <h3 className="text-lg font-semibold mb-2">Unlock Full Financial Transparency</h3>
        <p className="text-foreground-muted mb-4 max-w-md mx-auto text-sm">
          Log in to access detailed financials, budget breakdown, P&L, and key documents.
        </p>

        {/* Locked Sections List */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {financials.locked_sections?.map((section, index) => (
            <Badge key={index} variant="outline" className="bg-white/5">
              <Lock className="h-3 w-3 mr-1" />
              {section}
            </Badge>
          ))}
        </div>

        {/* Blurred Preview Teaser */}
        <div className="relative overflow-hidden rounded-lg mb-6 max-w-md mx-auto">
          <div className="blur-sm opacity-50">
            <div className="bg-white/5 p-3 border-b border-white/10">
              <div className="flex justify-between text-sm">
                <span>Acquisition</span>
                <span>€•••,•••</span>
              </div>
            </div>
            <div className="bg-white/5 p-3 border-b border-white/10">
              <div className="flex justify-between text-sm">
                <span>Renovation</span>
                <span>€••,•••</span>
              </div>
            </div>
            <div className="bg-white/5 p-3">
              <div className="flex justify-between text-sm">
                <span>Legal & Fees</span>
                <span>€•,•••</span>
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/80"></div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={handleLoginClick} className="rounded-full">
            Log in to View Full Financials
          </Button>
          <Button variant="outline" onClick={handleLoginClick} className="rounded-full">
            Create Account
          </Button>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Financial Summary - Always Visible */}
      {renderFinancialSummary()}

      {/* Locked Panel for Non-Logged-In Users */}
      {!isLoggedIn && renderLockedPanel()}

      {/* Full Financial Data for Logged-In Users */}
      {isLoggedIn && (
        <>
          {/* Budget Investment & Performance */}
          {renderBudgetAndPnL()}

          {/* Budget vs actual, phase by phase */}
          <PhaseFinancialsSection projectId={projectId} />

          {/* Actual P&L */}
          {renderActualPnL()}

          {/* Documents */}
          {renderDocuments()}
        </>
      )}
    </div>
  );
};

export default ProjectFinancialsSection;
